import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Sparkles, BookOpen, FileText, BarChart3, PencilLine, GitCompare, ShieldAlert,
  Quote, TriangleAlert, Settings, Send, X,
} from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const MODES = [
  { key: "ask_knowledge", label: "Ask Knowledge", icon: BookOpen, hint: "Grounded answers from published policies with exact-version citations." },
  { key: "summarize", label: "Summarize", icon: FileText, hint: "Summarize an authorized source without inventing facts." },
  { key: "operational_review", label: "Operational Review", icon: BarChart3, hint: "Review application-computed reconciliation figures in your scope." },
  { key: "draft", label: "Draft", icon: PencilLine, hint: "Prepare a non-authoritative draft you must review and confirm." },
  { key: "compare", label: "Compare", icon: GitCompare, hint: "Compare figures across cases; no invented numbers or causation." },
];

const LABEL_STYLE: Record<string, { cls: string; text: string }> = {
  fact: { cls: "bg-status-good/12 text-status-good ring-status-good/25", text: "Source fact" },
  calculation: { cls: "bg-status-info/12 text-status-info ring-status-info/25", text: "App calculation" },
  commentary: { cls: "bg-muted text-muted-foreground ring-border", text: "Commentary" },
  missing: { cls: "bg-status-warn/12 text-status-warn ring-status-warn/25", text: "Missing / conflicting" },
  inference: { cls: "bg-brand/12 text-brand ring-brand/25", text: "AI inference" },
};

const STATE_INFO: Record<string, { title: string; desc: string; tone: string }> = {
  disabled: { title: "AI assistant is turned off", desc: "Your tenant administrator has disabled the assistant. Core search, reports and workflows remain fully available.", tone: "warn" },
  not_configured: { title: "AI provider not configured", desc: "No working AI provider is configured. Deterministic platform features remain available.", tone: "warn" },
  unavailable: { title: "AI is temporarily unavailable", desc: "The provider could not be reached. Please try again shortly — nothing else is affected.", tone: "warn" },
  timeout: { title: "AI request timed out", desc: "The provider took too long. Please try again.", tone: "warn" },
  rate_limited: { title: "Request limit reached", desc: "The tenant's request limit for today has been reached. Deterministic features remain usable.", tone: "warn" },
  budget_exhausted: { title: "Token budget exhausted", desc: "The tenant's token budget for today is exhausted. Deterministic features remain usable.", tone: "warn" },
  insufficient: { title: "Insufficient authorized evidence", desc: "There isn't enough authorized, published information to answer. See the limitations below.", tone: "warn" },
};

interface Citation { id: string; source_id: string; source_type: string; version: number | null; title: string; snippet: string; link?: string; }
interface Segment { label: string; text: string; citations: string[]; }
interface AIResponse {
  request_id?: string; mode: string; state: string; provider_ref?: string; model?: string;
  config_version?: number; segments: Segment[]; citations: Citation[]; limitations: string[];
  safety?: { injection_flagged: boolean; stripped_citations: number; stripped_figures: number };
  human_review_required: boolean; draft?: any;
}

const StateBanner: React.FC<{ state: string }> = ({ state }) => {
  const info = STATE_INFO[state];
  if (!info) return null;
  const tone = info.tone === "warn" ? "border-status-warn/30 bg-status-warn/8 text-status-warn" : "border-border bg-muted";
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 ${tone}`} data-testid="assist-state">
      <ShieldAlert size={18} className="mt-0.5 shrink-0" />
      <div>
        <div className="text-sm font-semibold">{info.title}</div>
        <div className="mt-0.5 text-xs opacity-90">{info.desc}</div>
      </div>
    </div>
  );
};

const DraftConfirm: React.FC<{ resp: AIResponse; onDone: () => void }> = ({ resp, onDone }) => {
  const {
    t: tI18n
  } = useI18n();

  const { hasPerm } = useAuth();
  const d = resp.draft || {};
  const dtype: string = d.type || "communication";
  const [title, setTitle] = useState(d.title || "");
  const [body, setBody] = useState(d.body || "");
  const [priority, setPriority] = useState("normal");
  const [requiresAck, setRequiresAck] = useState(false);
  const [category, setCategory] = useState("General");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  // targeting
  const [scopeType, setScopeType] = useState("tenant");
  const [tree, setTree] = useState<{ regions: any[]; districts: any[]; stores: any[] } | null>(null);
  const [storeQuery, setStoreQuery] = useState("");
  const [storeOpts, setStoreOpts] = useState<any[]>([]);
  const [scopeId, setScopeId] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [ticketOpts, setTicketOpts] = useState<any[]>([]);
  const [visitId, setVisitId] = useState("");
  const [visitOpts, setVisitOpts] = useState<any[]>([]);
  const [me, setMe] = useState<any>(null);

  const DEST_PERM: Record<string, string> = {
    communication: "comm.send", task: "task.manage", action: "mc.run",
    ticket_response: "support.submit", audit_follow_up: "assure.conduct",
    learning_outline: "learn.author", labor_plan_observation: "intel.manage", leadership_note: "intel.manage",
  };
  const canConfirm = hasPerm(DEST_PERM[dtype]);
  const needsStore = dtype === "action";
  const needsTicket = dtype === "ticket_response";
  const needsVisit = dtype === "audit_follow_up";
  const isComm = dtype === "communication";

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data.user || r.data)).catch(() => {});
    if (isComm && hasPerm("org.view")) api.get("/org/tree").then((r) => setTree(r.data)).catch(() => setTree(null));
    if (needsTicket) api.get("/support/tickets?limit=20").then((r) => setTicketOpts(r.data.tickets || [])).catch(() => {});
    if (needsVisit) api.get("/assure/visits?limit=20").then((r) => setVisitOpts((r.data.visits || []).slice(0, 20))).catch(() => {});
  }, []); // eslint-disable-line

  // bounded, scope-enforced store search (no unbounded reads)
  useEffect(() => {
    if (!(needsStore || (dtype === "labor_plan_observation") || (dtype === "leadership_note") || (isComm && scopeType === "store"))) return;
    const t = setTimeout(() => {
      api.get(`/org/stores?limit=20&q=${encodeURIComponent(storeQuery)}`).then((r) => setStoreOpts(r.data.stores || [])).catch(() => setStoreOpts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [storeQuery, needsStore, dtype, isComm, scopeType]);

  const uid = me?.id;
  const scopeSummary = () => {
    if (isComm) {
      if (scopeType === "tenant") return "Everyone in the tenant";
      const src = scopeType === "region" ? tree?.regions : scopeType === "district" ? tree?.districts : storeOpts;
      const found = (src || []).find((x: any) => x.id === scopeId);
      return found ? `${scopeType}: ${found.name || found.store_number}` : `${tl("Select a {scope}", { scope: scopeType })}`;
    }
    if (needsStore || dtype === "labor_plan_observation" || dtype === "leadership_note") {
      const found = storeOpts.find((x) => x.id === scopeId);
      return scopeId ? `${tl("Store:")} ${found ? found.name : scopeId}` : (needsStore ? tl("Select a store (required)") : tl("Tenant / no specific store"));
    }
    if (needsTicket) return ticketId ? tl("Selected ticket") : tl("Select a ticket");
    if (needsVisit) return visitId ? tl("Selected visit") : tl("Select a visit");
    return "Tenant-level draft";
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const payload: any = { title, body, priority, category };
      if (isComm) {
        payload.requires_ack = requiresAck;
        payload.audience = { scope_type: scopeType, roles: [] };
        if (scopeType !== "tenant") payload.audience.scope_id = scopeId || null;
      }
      if (needsStore || dtype === "labor_plan_observation" || dtype === "leadership_note") payload.store_id = scopeId || null;
      if (dtype === "labor_plan_observation" || dtype === "leadership_note") payload.owner_user_id = uid;
      if (needsTicket) payload.ticket_id = ticketId;
      if (needsVisit) { payload.visit_id = visitId; payload.owner_user_id = uid; payload.due_date = dueDate; }
      if (dtype === "action") payload.due_date = dueDate;
      const r = await api.post(`/assist/requests/${resp.request_id}/confirm`, payload);
      toast.success(`${tl("Confirmed — {type} created", { type: r.data.record?.type || "record" })}${r.data.recipient_count != null ? tl(" ({n} recipients)", { n: r.data.recipient_count }) : ""}`);
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not confirm — check permissions and scope");
    } finally { setBusy(false); }
  };
  const reject = async () => {
    try { await api.post(`/assist/requests/${resp.request_id}/reject`, { reason: "Discarded by reviewer" }); toast.success("Draft discarded"); onDone(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Could not discard"); }
  };
  const confirmDisabled = busy || !canConfirm
    || (needsStore && !scopeId)
    || (isComm && scopeType !== "tenant" && !scopeId)
    || (needsTicket && !ticketId)
    || (needsVisit && (!visitId || !dueDate));

  const storePicker = (
    <div>
      <Label className="text-xs">{tI18n("Store")}{" "}{needsStore ? tI18n("(required)") : tI18n("(optional)")}</Label>
      <Input placeholder={tI18n("Search store name/number…")} value={storeQuery} onChange={(e) => setStoreQuery(e.target.value)} className="mb-1" data-testid="assist-store-search" />
      <Select value={scopeId} onValueChange={setScopeId}>
        <SelectTrigger data-testid="assist-draft-scopeid"><SelectValue placeholder={tI18n("Select a store")} /></SelectTrigger>
        <SelectContent>{storeOpts.map((s) => <SelectItem key={s.id} value={s.id}>#{s.store_number} · {s.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    (<Card className="border-brand/30 p-4" data-testid="assist-draft">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-brand">
        <PencilLine size={15} />{" "}{tI18n("Draft")}{" "}{dtype.replace(/_/g, " ")}{" "}{tI18n("— review before it takes effect")}</div>
      <div className="mb-2 rounded-md bg-status-warn/8 px-3 py-2 text-xs text-status-warn">{tI18n(
        "This is an AI-generated draft. It is not an operational record until you confirm it, which routes it through the destination module's normal service, permissions and audit."
      )}</div>
      <div className="space-y-3">
        <div><Label className="text-xs">{tI18n("Title")}</Label><Input data-testid="assist-draft-title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div><Label className="text-xs">{tI18n("Content")}</Label><Textarea data-testid="assist-draft-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></div>

        {isComm && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs">{tI18n("Audience scope")}</Label>
              <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(""); }}>
                <SelectTrigger data-testid="assist-draft-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">{tI18n("Whole tenant")}</SelectItem>
                  {hasPerm("org.view") && <SelectItem value="region">{tI18n("Region")}</SelectItem>}
                  {hasPerm("org.view") && <SelectItem value="district">{tI18n("District")}</SelectItem>}
                  <SelectItem value="store">{tI18n("A store")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(scopeType === "region" || scopeType === "district") && (
              <div>
                <Label className="text-xs capitalize">{scopeType}</Label>
                <Select value={scopeId} onValueChange={setScopeId}>
                  <SelectTrigger data-testid="assist-draft-scopeid"><SelectValue placeholder={`${tl("Select {scope}", { scope: scopeType })}`} /></SelectTrigger>
                  <SelectContent>
                    {((scopeType === "region" ? tree?.regions : tree?.districts) || []).map((x: any) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {scopeType === "store" && storePicker}
            <div>
              <Label className="text-xs">{tI18n("Priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">{tI18n("Normal")}</SelectItem><SelectItem value="important">{tI18n("Important")}</SelectItem><SelectItem value="urgent">{tI18n("Urgent")}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}

        {(needsStore || dtype === "labor_plan_observation" || dtype === "leadership_note") && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {storePicker}
            {dtype === "action" && <div><Label className="text-xs">{tI18n("Due date")}</Label><Input type="date" data-testid="assist-draft-due" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>}
          </div>
        )}

        {needsTicket && (
          <div>
            <Label className="text-xs">{tI18n("Ticket (requester-visible reply)")}</Label>
            <Select value={ticketId} onValueChange={setTicketId}>
              <SelectTrigger data-testid="assist-draft-ticket"><SelectValue placeholder={tI18n("Select a ticket")} /></SelectTrigger>
              <SelectContent>{ticketOpts.map((t) => <SelectItem key={t.id} value={t.id}>#{t.number} · {t.subject}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {needsVisit && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{tI18n("Visit")}</Label>
              <Select value={visitId} onValueChange={setVisitId}>
                <SelectTrigger data-testid="assist-draft-visit"><SelectValue placeholder={tI18n("Select a visit")} /></SelectTrigger>
                <SelectContent>{visitOpts.map((v) => <SelectItem key={v.id} value={v.id}>{v.template_title || "Visit"} · {v.status}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{tI18n("Due date")}</Label><Input type="date" data-testid="assist-draft-due" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
          </div>
        )}

        {(dtype === "task" || dtype === "learning_outline") && (
          <div><Label className="text-xs">{tI18n("Category")}</Label><Input data-testid="assist-draft-category" value={category} onChange={(e) => setCategory(e.target.value)} /></div>
        )}

        {isComm && <div className="flex items-center gap-2"><Switch checked={requiresAck} onCheckedChange={setRequiresAck} data-testid="assist-draft-ack" /><Label className="text-xs">{tI18n("Require acknowledgement")}</Label></div>}
      </div>
      <div className="mt-3 rounded-md bg-secondary px-3 py-2 text-xs" data-testid="assist-scope-summary">
        <span className="font-semibold">{tI18n("Affected scope:")}</span> {scopeSummary()}
        {isComm && <span className="ml-1 text-muted-foreground">{tI18n("· exact recipients are resolved and reported on send")}</span>}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" onClick={confirm} disabled={confirmDisabled} data-testid="assist-confirm-btn"><Send size={14} className="mr-1" />{tI18n("Confirm")}</Button>
        <Button size="sm" variant="outline" onClick={reject} data-testid="assist-reject-btn"><X size={14} className="mr-1" />{tI18n("Discard")}</Button>
        {!canConfirm && <span className="text-xs text-status-warn">{tI18n("You can draft but not confirm this type.")}</span>}
      </div>
    </Card>)
  );
};

export default function Assist() {
  const {
    t
  } = useI18n();

  const { modules, hasPerm } = useAuth();
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("ask_knowledge");
  const [query, setQuery] = useState("");
  const [draftType, setDraftType] = useState("communication");
  const [running, setRunning] = useState(false);
  const [resp, setResp] = useState<AIResponse | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/assist/status"); setStatus(r.data); }
    catch { setStatus(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (modules.assist === false) {
    return <div><PageHeader title="AI Assistant" testId="assist-header" /><EmptyState icon={<Sparkles size={28} />} title="AI Assistant module is inactive" description="Enable it in Admin → Modules." /></div>;
  }
  if (loading) return <div><PageHeader title="AI Assistant" testId="assist-header" /><LoadingBlock /></div>;
  if (!hasPerm("ai.use")) {
    return <div><PageHeader title="AI Assistant" testId="assist-header" /><EmptyState icon={<ShieldAlert size={28} />} title="Access restricted" description="You do not have permission to use the assistant." /></div>;
  }
  if (status && !status.permitted) {
    return <div><PageHeader title="AI Assistant" testId="assist-header" /><EmptyState icon={<ShieldAlert size={28} />} title="Not enabled for your role" description="Your tenant has not permitted your role to use the assistant." /></div>;
  }

  const enabledModes = MODES.filter((m) => status?.modes?.[m.key]);
  const activeMode = MODES.find((m) => m.key === mode) || MODES[0];

  const run = async () => {
    if (!query.trim()) return;
    setRunning(true); setResp(null);
    try {
      const payload: any = { mode, query };
      if (mode === "draft") payload.draft_type = draftType;
      const r = await api.post("/assist/query", payload);
      setResp(r.data);
      await loadStatus();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setRunning(false); }
  };

  const DRAFT_TYPE_OPTS = [
    { v: "communication", l: "Communication", perm: "comm.send" },
    { v: "task", l: "Task template", perm: "task.manage" },
    { v: "action", l: "Action", perm: "mc.run" },
    { v: "ticket_response", l: "Ticket response", perm: "support.submit" },
    { v: "audit_follow_up", l: "Audit follow-up (CAPA)", perm: "assure.conduct" },
    { v: "learning_outline", l: "Learning outline", perm: "learn.author" },
    { v: "labor_plan_observation", l: "Labor-plan observation", perm: "intel.manage" },
    { v: "leadership_note", l: "Leadership note", perm: "intel.manage" },
  ].filter((o) => hasPerm(o.perm));

  const citationById = (id: string) => resp?.citations.find((c) => c.id === id);

  return (
    (<div data-testid="assist-page">
      <PageHeader
        title="AI Knowledge & Operational Assistant"
        subtitle="Grounded, permission-aware answers. AI output is advisory — always review before acting."
        testId="assist-header"
        actions={status?.can_admin ? (
          <Button size="sm" variant="outline" onClick={() => setShowAdmin((v) => !v)} data-testid="assist-admin-toggle">
            <Settings size={14} className="mr-1" />{t("Governance")}</Button>
        ) : undefined}
      />
      {status?.provider_status && status.provider_status !== "ready" && <div className="mb-4"><StateBanner state={status.provider_status === "not_configured" ? t("not_configured") : status.provider_status} /></div>}
      {!status?.enabled && <div className="mb-4"><StateBanner state="disabled" /></div>}
      {showAdmin && status?.can_admin && <AdminPanel onChange={loadStatus} />}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5 mb-4">
        {enabledModes.map((m) => (
          <button
            key={m.key}
            onClick={() => { setMode(m.key); setResp(null); }}
            data-testid={`assist-mode-${m.key}`}
            className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${mode === m.key ? "border-brand bg-brand/5 ring-1 ring-brand/30" : "border-border hover:bg-secondary"}`}
          >
            <m.icon size={16} className={mode === m.key ? t("text-brand mt-0.5") : t("text-muted-foreground mt-0.5")} />
            <span className="text-xs font-medium">{m.label}</span>
          </button>
        ))}
      </div>
      <Card className="p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><activeMode.icon size={15} className="text-brand" />{activeMode.label}</div>
        <p className="mb-3 text-xs text-muted-foreground">{activeMode.hint}</p>
        {mode === "draft" && (
          <div className="mb-3">
            <Label className="text-xs">{t("Draft type")}</Label>
            <Select value={draftType} onValueChange={setDraftType}>
              <SelectTrigger className="max-w-xs" data-testid="assist-draft-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DRAFT_TYPE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Textarea
          data-testid="assist-query-input"
          rows={3}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={mode === "ask_knowledge" ? t("Ask about a published policy or procedure…") : mode === "draft" ? "Describe the communication you want to draft…" : "Describe what you want to review…"}
        />
        <div className="mt-3 flex items-center gap-2">
          <Button onClick={run} disabled={running || !query.trim()} data-testid="assist-run-btn">
            <Sparkles size={14} className="mr-1" />{running ? t("Working…") : t("Run")}
          </Button>
          <span className="text-xs text-muted-foreground">{t("Sources are permission-filtered to what you can access.")}</span>
        </div>
      </Card>
      {resp && (
        <div className="mt-4 space-y-4" data-testid="assist-response">
          {STATE_INFO[resp.state] && <StateBanner state={resp.state} />}

          {resp.human_review_required && resp.state === "answered" && (
            <div className="flex items-center gap-2 rounded-md bg-brand/8 px-3 py-2 text-xs text-brand" data-testid="assist-review-warning">
              <TriangleAlert size={14} />{" "}{t(
                "AI output is advisory and not an authoritative record. Verify before acting."
              )}</div>
          )}

          {resp.draft && <DraftConfirm resp={resp} onDone={() => setResp(null)} />}

          {resp.segments.length > 0 && (
            <Card className="p-4">
              <div className="space-y-3">
                {resp.segments.map((seg, i) => {
                  const st = LABEL_STYLE[seg.label] || LABEL_STYLE.inference;
                  return (
                    <div key={i} data-testid={`assist-segment-${i}`} className="border-l-2 border-border pl-3">
                      <span data-testid={`assist-segment-label-${i}`} className={`mb-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${st.cls}`}>{st.text}</span>
                      <p className="whitespace-pre-wrap text-sm text-foreground">{seg.text}</p>
                      {seg.citations.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {seg.citations.map((cid) => {
                            const c = citationById(cid);
                            if (!c) return null;
                            return <span key={cid} className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">[{c.title}{c.version ? ` v${c.version}` : ""}]</span>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {resp.citations.length > 0 && (
            <Card className="p-4" data-testid="assist-citations">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Quote size={13} />{t("Citations (exact authorized versions)")}</div>
              <div className="space-y-2">
                {resp.citations.map((c, i) => (
                  <div key={c.id} data-testid={`assist-citation-${i}`} className="rounded-md border border-border p-2 text-xs">
                    <div className="font-medium">{c.title}{c.version ? <span className="ml-1 text-muted-foreground">{t("· version")}{" "}{c.version}</span> : null} <span className="ml-1 rounded bg-secondary px-1 py-0.5 text-[10px] uppercase text-muted-foreground">{c.source_type}</span></div>
                    <div className="mt-0.5 line-clamp-2 text-muted-foreground">{c.snippet}</div>
                    {c.link && <a href={c.link} className="mt-0.5 inline-block text-brand hover:underline">{t("Open source →")}</a>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {resp.limitations.length > 0 && (
            <Card className="border-status-warn/30 p-4" data-testid="assist-limitations">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-status-warn"><TriangleAlert size={13} />{t("Limitations")}</div>
              <ul className="list-inside list-disc space-y-0.5 text-xs text-muted-foreground">
                {resp.limitations.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </Card>
          )}

          {resp.safety && (resp.safety.injection_flagged || resp.safety.stripped_citations > 0 || resp.safety.stripped_figures > 0) && (
            <div className="text-[11px] text-muted-foreground" data-testid="assist-safety">{t("Safety:")}{" "}{resp.safety.injection_flagged && "instruction-like content treated as data · "}
              {resp.safety.stripped_citations > 0 && tl("{n} unverifiable citation(s) removed · ", { n: resp.safety.stripped_citations })}
              {resp.safety.stripped_figures > 0 && `${resp.safety.stripped_figures} unverifiable figure(s) reclassified`}
            </div>
          )}

          {resp.provider_ref && (
            <div className="text-[11px] text-muted-foreground">{t("Provider:")}{" "}<span className="font-medium">{resp.provider_ref === "deterministic-test" ? t("Deterministic test adapter (not live AI)") : resp.model}</span>{" "}{t("· config v")}{resp.config_version}
            </div>
          )}
        </div>
      )}
    </div>)
  );
}

const AdminPanel: React.FC<{ onChange: () => void }> = ({ onChange }) => {
  const {
    t
  } = useI18n();

  const [config, setConfig] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([api.get("/assist/config"), api.get("/assist/usage")]);
      setConfig(c.data.config); setUsage(u.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!config) return <Card className="mb-4 p-4"><LoadingBlock /></Card>;

  const save = async (patch: any) => {
    if (!reason.trim()) { toast.error("A change reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/assist/config", { ...patch, reason });
      toast.success("Governance updated (new version)");
      setReason("");
      await load(); onChange();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    (<Card className="mb-4 space-y-4 p-4" data-testid="assist-admin-panel">
      <div className="flex items-center gap-2 text-sm font-semibold"><Settings size={15} className="text-brand" />{t("AI Governance (v")}{config.version})</div>
      <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Provider")}</div><div className="font-medium">{config.provider_ref}</div></div>
        <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Model")}</div><div className="font-medium">{config.model}</div></div>
        <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Provider status")}</div><div className="font-medium capitalize">{(config.provider_status || "").replace(/_/g, " ")}</div></div>
        <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Retention")}</div><div className="font-medium">{config.retention_days}{" "}{t("days")}</div></div>
      </div>
      {usage && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Requests today")}</div><div className="font-medium">{usage.requests} / {usage.limits?.daily_request_limit}</div></div>
          <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Tokens today")}</div><div className="font-medium">{usage.tokens} / {usage.limits?.daily_token_ceiling}</div></div>
          <div className="rounded-md border border-border p-2"><div className="text-muted-foreground">{t("Cost cap (mo)")}</div><div className="font-medium">${usage.limits?.monthly_cost_ceiling_usd}</div></div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder={t("Change reason (required)")} value={reason} onChange={(e) => setReason(e.target.value)} className="max-w-xs" data-testid="assist-config-reason" />
        <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1">
          <Switch checked={config.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={busy} data-testid="assist-config-enabled" />
          <Label className="text-xs">{config.enabled ? t("Enabled") : t("Disabled")}</Label>
        </div>
        <Select value={config.provider_ref} onValueChange={(v) => save({ provider_ref: v })}>
          <SelectTrigger className="w-56" data-testid="assist-config-provider"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="emergent">{t("emergent (live)")}</SelectItem>
            <SelectItem value="deterministic-test">{t("deterministic-test (test only)")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-[11px] text-muted-foreground">{t(
        "Each change creates a new immutable version; historical AI requests keep the version that governed them."
      )}</p>
    </Card>)
  );
};
